import { CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { agGridThemeActivityLog } from '@app/ag-grid-custom-theme';
import { ActivityLogPropertyTypes } from '@models/enums/ActivityLogPropertyTypes';
import { AwaiterService } from '@services/awaiter.service';
import { ActivityLogService } from '@services/http/clients/activity-log.service';
import { ActivityLogView, EntityPropertyValueChangedDto } from '@services/http/clients/api-proxies';
import { ClientSideGridComponent } from '@shared/grids/client-side-grid/client-side-grid.component';
import { formatDateTimeDisplay } from '@utils/date-utils';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { ColDef } from 'ag-grid-enterprise';
import { IconSanitizerService, ModalComponentBase } from 'ngx-lib';
import { createActivityLogDetailsColumnDefs } from './modal-activity-log-details.config';

interface IModalActivityLogDetailsParameters {
    id: number;
}

@Component({
    selector: 'app-modal-activity-log-details',
    templateUrl: './modal-activity-log-details.component.html',
    styleUrls: ['./modal-activity-log-details.component.scss'],
    imports: [ClientSideGridComponent, ...CDK_IMPORTS],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DatePipe, CurrencyPipe, PercentPipe]
})
export class ModalActivityLogDetailsComponent
    extends ModalComponentBase<IModalActivityLogDetailsParameters, undefined>
    implements OnInit
{
    activityLog = signal<ActivityLogView | undefined>(undefined);
    changedProps = signal<EntityPropertyValueChangedDto[] | undefined>(undefined);
    editedDate = signal<string | undefined>(undefined);
    eventMessage = signal<string | undefined>(undefined);
    userFullName = signal<string | undefined>(undefined);
    closeIcon: SafeHtml;
    errorMessage = signal<string | null>(null);
    activityLogPropertyTypes = ActivityLogPropertyTypes;

    readonly columnDefs = computed<ColDef<EntityPropertyValueChangedDto>[]>(() =>
        createActivityLogDetailsColumnDefs((v, t) => this.formatValue(v, t))
    );
    readonly defaultColDef = {
        resizable: false,
        sortable: false,
        filter: false,
        editable: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        lockVisible: true,
        cellStyle: { whiteSpace: 'normal' }
    };

    themeConfig = agGridThemeActivityLog;

    private readonly activityLogService = inject(ActivityLogService);
    private readonly awaiter = inject(AwaiterService);
    private readonly datePipe = inject(DatePipe);
    private readonly currencyPipe = inject(CurrencyPipe);
    private readonly percentPipe = inject(PercentPipe);
    private readonly iconSanitizer = inject(IconSanitizerService);

    constructor() {
        super();
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
    }

    ngOnInit(): void {
        this.loadActivityLog();
    }

    formatValue(value: any, type?: number): any {
        if (value === null) return 'None';

        switch (type) {
            case ActivityLogPropertyTypes.Currency:
                return this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
            case ActivityLogPropertyTypes.Date:
                return this.datePipe.transform(value, 'MM/dd/y');
            case ActivityLogPropertyTypes.DateTime:
                return this.datePipe.transform(value, 'MM/dd/y hh:mm a');
            case ActivityLogPropertyTypes.Percentage:
                return this.percentPipe.transform(value);
            case ActivityLogPropertyTypes.List:
                return Array.isArray(value) ? value.join(', ') : value;
            case ActivityLogPropertyTypes.Boolean:
                return value ? 'Yes' : 'No';
            default:
                return value;
        }
    }

    private async loadActivityLog(): Promise<void> {
        if (!this.parameters?.id) {
            this.errorMessage.set('No record specified.');
            return;
        }
        const result = await this.awaiter.awaitAction('Loading activity log', async () => {
            const log = await this.activityLogService.getById(this.parameters!.id);
            this.activityLog.set(log);
            this.changedProps.set(log?.changedProperties);
            this.userFullName.set(log?.userFullName);
            this.editedDate.set(formatDateTimeDisplay(log?.date));
            this.eventMessage.set(log?.message);
            return log;
        });
        if (!result) this.errorMessage.set('Unable to load activity log details.');
    }
}
